/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/**
 * Single source of every user-visible string on the patient Medaica Report download
 * page, so the whole file can go to label control as one reviewable unit (the
 * same arrangement as the booking wizard's bookingCopy). Interpolated strings are
 * functions so the copy stays in this file.
 *
 * Copy rules: no clinical findings or diagnosis language anywhere on the page —
 * the page only names each document and who it is for, and hands over the PDF; it
 * never describes what either document says. The disclaimer's "not a diagnosis" is
 * the one deliberate use of the word, and it negates it.
 */
const heartReportCopy = {
  appName: "Medaica Report",
  supportEmail: "support@medaica.com",

  // Screen 1 — landing. Shows nothing about the patient: the masked email only becomes
  // known once a code has been sent, so the subtitle here is generic.
  requestCode: {
    title: "Your Medaica Report is ready",
    subtitle: "To protect your privacy, we'll send a 6-digit code to the email address this report was sent to.",
    sendButton: "Send me a code",
    notYouPrompt: "Not you, or wrong address?",
    contactSupport: "Contact support.",
    notices: {
      sessionEnded: "Your session ended. Send a new code to continue.",
      codeUsed: "That code was already used. Send a new code to continue.",
    },
    errors: {
      sendFailed: "We couldn't send the code right now. Try again in a minute.",
    },
  },

  // Screen 2 — the code gate.
  verify: {
    title: "Enter your code",
    subtitle: (maskedEmail: string): string =>
      `We've emailed a 6-digit code to ${maskedEmail}. It expires in 10 minutes.`,
    continueButton: "Continue",
    didntGetIt: "Didn't get it?",
    resend: "Resend code",
    // Deliberately non-committal: the resend response is neutral (a normal-looking reply even
    // when throttled), so the client can't truthfully promise a NEW code was sent.
    resent: "Check your email for the latest code — it can take a minute to arrive.",
    // Shown after several failed attempts — calmer, actionable guidance plus a way out.
    stuckHelp: "Still not working? Use the code from your most recent email, or request a new one. Need help?",
    contactSupport: "Contact support.",
    errors: {
      wrongCode: (attemptsRemaining: number): string =>
        `That code isn't right. ${attemptsRemaining} attempt${attemptsRemaining === 1 ? "" : "s"} left.`,
      wrongCodeGeneric: "That code isn't right. Please try again.",
      expired: "That code has expired.",
      locked: "Too many attempts. Request a new code or contact support.",
      sendFailed: "We couldn't send the code right now. Try again in a minute.",
      generic: "Something went wrong. Please try again.",
    },
  },

  // Screen 3 — the download. Header facts only; nothing from either document's body is rendered.
  download: {
    title: (patientName: string | null): string =>
      patientName ? `Medaica Report for ${patientName}` : "Your Medaica Report",
    examDate: (examDate: string): string => `Exam date: ${examDate}`,
    released: (releasedAt: string): string => `Released: ${releasedAt}`,
    // Separator between the header facts above.
    factsSeparator: " · ",
    downloadButton: "Download Medaica Report (PDF)",
    // Helper line under the main button. Shown only together with the doctor's copy below,
    // to tell the two documents apart.
    ownReportNote: "A summary written for you.",
    // The second, clinician-format PDF, offered only when the report comes with one. Framed
    // by who it is for — never by a formal report title — so the patient's own report stays
    // the one obvious download.
    doctorCopy: {
      heading: "Seeing a doctor?",
      body:
        "Share this detailed version with your doctor or other health care provider. " +
        "It is written for clinicians — discuss it with your provider.",
      downloadButton: "Download report for your doctor (PDF)",
    },
    disclaimer:
      "This report is a screening summary prepared by your care team. It is not a diagnosis. " +
      "If you have symptoms or questions, contact your provider. In an emergency call 911.",
    sessionNote: "Done? Close this tab. Your session ends automatically after 15 minutes.",
    errors: {
      downloadFailed: "We couldn't download the report right now. Please try again.",
      doctorCopyDownloadFailed: "We couldn't download the report for your doctor right now. Please try again.",
    },
  },

  // Screen 0 — the link can't be used. One view, copy picked by reason. An unknown or
  // malformed token reads as expired (the API reports it that way; nothing to learn from it).
  unavailable: {
    expired: {
      title: "This link has expired.",
      body: "Contact your care team or Medaica support to request a new one.",
    },
    unavailable: {
      title: "This report is no longer available.",
      body: "Your care team may be updating it; you'll receive a new email when it's ready.",
    },
    contactSupport: "Contact Medaica support",
  },
}

export default heartReportCopy
